import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useDeviceCoverage, type CoverageDevice, type CoverageState } from '@/api/insights';
import { PageHeader } from '@/components/PageHeader';
import { StatCard } from '@/components/dashboard/StatCard';
import { DesktopOnly } from '@/components/dashboard/DesktopOnly';
import { SearchInput } from '@/components/ui/search-input';
import { fmtAge } from '@/lib/rmm-helpers';
import { ROUTES, companyPath, insightsDevicePath } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * Insights → Device coverage. Every device across Datto RMM and Intune as one row: is it in both,
 * do their check-ins agree (drift), and is anything duplicated. Gaps are category-aware — a PC
 * missing from Intune only counts when the client actually uses Intune (server/insights/devices.ts).
 */

type Filter = 'gaps' | 'drift' | 'rmm-only' | 'intune-only' | 'no-remote' | 'duplicates' | 'in-sync' | 'all';
type Tab = 'devices' | 'companies';

const noRemote = (d: CoverageDevice) => d.remote?.state === 'missing';
const FILTERS: { id: Filter; label: string; test: (d: CoverageDevice) => boolean }[] = [
  { id: 'gaps',        label: 'All gaps',        test: (d) => d.state === 'rmm-only' || d.state === 'intune-only' || d.state === 'drift' || d.flags.length > 0 || noRemote(d) },
  { id: 'intune-only', label: 'Not in Datto RMM', test: (d) => d.state === 'intune-only' },
  { id: 'rmm-only',    label: 'Not in Intune',    test: (d) => d.state === 'rmm-only' },
  { id: 'no-remote',   label: 'No ScreenConnect', test: noRemote },
  { id: 'drift',       label: 'Check-in drift',   test: (d) => d.state === 'drift' },
  { id: 'duplicates',  label: 'Duplicates & stale', test: (d) => d.flags.length > 0 },
  { id: 'in-sync',     label: 'In sync',          test: (d) => d.state === 'in-sync' },
  { id: 'all',         label: 'All devices',      test: () => true },
];

const STATE_CHIP: Record<CoverageState, { label: string; cls: string }> = {
  'in-sync':     { label: 'In sync',          cls: 'bg-success-soft text-success' },
  drift:         { label: 'Drift',            cls: 'bg-warning-soft text-warning' },
  'rmm-only':    { label: 'Not in Intune',    cls: 'bg-destructive/10 text-destructive' },
  'intune-only': { label: 'Not in Datto RMM', cls: 'bg-destructive/10 text-destructive' },
  single:        { label: 'One source',       cls: 'bg-muted text-muted-foreground' },
};

const PREF = 'cosmos:insights:coverage';

export default function InsightsCoveragePage() {
  const [params, setParams] = useSearchParams();
  const saved = (() => { try { return JSON.parse(localStorage.getItem(PREF) ?? '{}') as { filter?: Filter; ignoreStale?: boolean }; } catch { return {}; } })();
  const [filter, setFilterState] = useState<Filter>(saved.filter || 'gaps');
  // Per-user checkbox; the number of days is the admin setting (Admin → Insights).
  const [ignoreStale, setIgnoreStaleState] = useState<boolean>(saved.ignoreStale ?? true);
  const { data, isLoading, error, isFetching } = useDeviceCoverage(!ignoreStale, { scoped: true });
  const [tab, setTab] = useState<Tab>('devices');
  const [q, setQ] = useState('');
  const persist = (p: { filter?: Filter; ignoreStale?: boolean }) => { try { localStorage.setItem(PREF, JSON.stringify({ filter, ignoreStale, ...p })); } catch { /* storage unavailable */ } };
  const setFilter = (f: Filter) => { setFilterState(f); persist({ filter: f }); };
  const setIgnoreStale = (v: boolean) => { setIgnoreStaleState(v); persist({ ignoreStale: v }); };

  // Menu views (?state=…&tab=…) replace the saved filter, then leave the URL.
  useEffect(() => {
    if (!params.has('state') && !params.has('tab')) return;
    const s = params.get('state') as Filter | null;
    if (s && FILTERS.some((f) => f.id === s)) { setFilter(s); setTab('devices'); }
    if (params.get('tab') === 'companies') setTab('companies');
    setParams({}, { replace: true });
  }, [params, setParams]);

  const devices = data?.devices ?? [];
  const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f.id, devices.filter(f.test).length])) as Record<Filter, number>, [devices]);
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    const test = FILTERS.find((f) => f.id === filter)!.test;
    return devices.filter(test).filter((d) => !s || d.hostname.toLowerCase().includes(s) || (d.companyName ?? '').toLowerCase().includes(s) || (d.serial ?? '').toLowerCase().includes(s) || (d.intune?.user ?? '').toLowerCase().includes(s));
  }, [devices, filter, q]);

  const both = devices.filter((d) => d.state === 'in-sync' || d.state === 'drift').length;
  const shouldBoth = both + counts['rmm-only'] + counts['intune-only'];
  const pct = shouldBoth ? Math.round((both / shouldBoth) * 1000) / 10 : null;

  return (
    <DesktopOnly title="Insights are available on desktop" message="Open Cosmos on a larger screen to view coverage.">
      <div className="mx-auto w-full max-w-[1600px] px-4 py-5 md:px-6">
        <PageHeader title="Device coverage" count={data ? (tab === 'devices' ? rows.length : data.companies.length) : null} isFetching={isFetching}
          subtitle="Datto RMM × Intune — one row per device, matched by serial, then network card (MAC), then hostname within the client"
          actions={tab === 'devices' && <SearchInput withIcon placeholder="Hostname, client, serial or user" className="w-72" value={q} onValueChange={setQ} />} />

        {isLoading ? (
          <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Correlating devices…</div>
        ) : error ? (
          <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">Failed to load: {error instanceof Error ? error.message : 'Unknown error'}</div>
        ) : data ? (
          <>
            {data.ignoredStale.settingDays > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                <label className="inline-flex cursor-pointer select-none items-center gap-1.5 text-foreground">
                  <input type="checkbox" className="h-3.5 w-3.5 accent-primary" checked={ignoreStale} onChange={(e) => setIgnoreStale(e.target.checked)} />
                  Ignore stale devices ({data.ignoredStale.settingDays}+ days without a check-in)
                </label>
                {isFetching && <Loader2 className="h-3 w-3 animate-spin" />}
                {ignoreStale && data.ignoredStale.days > 0 && (
                  <span>· {data.ignoredStale.rmm} Datto RMM agent{data.ignoredStale.rmm === 1 ? '' : 's'} and {data.ignoredStale.intune} Intune record{data.ignoredStale.intune === 1 ? '' : 's'} hidden</span>
                )}
                <span>· <Link to={ROUTES.admin.insights} className="hover:text-foreground hover:underline">change the number of days</Link></span>
              </div>
            )}
            {!data.sources.intune && (
              <div className="mt-3 rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-xs text-warning">Intune data isn't loaded yet (CIPP device sweep runs in the background) — only Datto RMM is shown.</div>
            )}
            {data.sources.screenconnect && !data.sources.screenconnectReady && (
              <div className="mt-3 rounded-md border border-info/30 bg-info-soft px-3 py-2 text-xs text-info">ScreenConnect machine details are still being read in the background — devices without a matched session show as "checking" until then, not as gaps.</div>
            )}
            <div className={cn('mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3', data.sources.screenconnect ? 'lg:grid-cols-6' : 'lg:grid-cols-5')}>
              <StatCard label="In both" value={pct != null ? `${pct}%` : '—'} sub={`${both} of ${shouldBoth} workstations`} tone={pct == null ? 'default' : pct >= 90 ? 'success' : 'warning'} onClick={() => { setTab('devices'); setFilter('in-sync'); }} />
              <StatCard label="Not in Datto RMM" value={counts['intune-only']} sub="Intune PCs/Macs, no agent" tone={counts['intune-only'] ? 'destructive' : 'success'} onClick={() => { setTab('devices'); setFilter('intune-only'); }} />
              <StatCard label="Not in Intune" value={counts['rmm-only']} sub="where the client uses Intune" tone={counts['rmm-only'] ? 'warning' : 'success'} onClick={() => { setTab('devices'); setFilter('rmm-only'); }} />
              {data.sources.screenconnect && (
                <StatCard label="No ScreenConnect" value={data.sources.screenconnectReady ? counts['no-remote'] : '…'} sub="Datto RMM computers, no session" tone={!data.sources.screenconnectReady ? 'default' : counts['no-remote'] ? 'warning' : 'success'} onClick={() => { setTab('devices'); setFilter('no-remote'); }} />
              )}
              <StatCard label="Check-in drift" value={counts.drift} sub={`last check-ins > ${data.driftHours} h apart`} tone={counts.drift ? 'warning' : 'success'} onClick={() => { setTab('devices'); setFilter('drift'); }} />
              <StatCard label="Duplicates & stale" value={counts.duplicates} sub="flagged, never merged" tone={counts.duplicates ? 'warning' : 'success'} onClick={() => { setTab('devices'); setFilter('duplicates'); }} />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <div className="flex gap-1 rounded-lg bg-muted p-1">
                {(['devices', 'companies'] as Tab[]).map((t) => (
                  <button key={t} type="button" onClick={() => setTab(t)}
                    className={cn('rounded-md px-3 py-1 text-xs font-medium', tab === t ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
                    {t === 'devices' ? 'Devices' : 'By client'}
                  </button>
                ))}
              </div>
              {tab === 'devices' && (
                <div className="flex flex-wrap gap-1 rounded-lg bg-muted p-1">
                  {FILTERS.filter((f) => f.id !== 'no-remote' || data.sources.screenconnect).map((f) => (
                    <button key={f.id} type="button" onClick={() => setFilter(f.id)}
                      className={cn('rounded-md px-3 py-1 text-xs font-medium', filter === f.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
                      {f.label} <span className="ml-1 tabular-nums text-muted-foreground">{counts[f.id]}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {tab === 'devices' ? <DeviceTable rows={rows} /> : <CompanyTable data={data} />}
            <p className="mt-2 text-[11px] text-muted-foreground">
              Gaps only count where the client has really rolled the product out (2+ workstations and at least a quarter of the other side), so a lone test
              device doesn't turn a whole client red. Servers aren't expected in Intune and phones aren't expected in RMM. Drift threshold:{' '}
              <Link to={ROUTES.admin.insights} className="hover:underline">Admin → Insights</Link>.
            </p>
          </>
        ) : null}
      </div>
    </DesktopOnly>
  );
}

function DeviceTable({ rows }: { rows: CoverageDevice[] }) {
  const shown = rows.slice(0, 500);
  return (
    <div className="mt-3 overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
      <table className="w-full text-sm" style={{ tableLayout: 'fixed', minWidth: 1100 }}>
        <colgroup><col style={{ width: 190 }} /><col style={{ width: 170 }} /><col style={{ width: 150 }} /><col style={{ width: 110 }} /><col style={{ width: 200 }} /><col style={{ width: 140 }} /><col style={{ width: 120 }} /><col /></colgroup>
        <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-3 py-2 font-medium">Device</th>
            <th className="px-3 py-2 font-medium">Client</th>
            <th className="px-3 py-2 font-medium">State</th>
            <th className="px-3 py-2 font-medium">Datto RMM</th>
            <th className="px-3 py-2 font-medium">Intune</th>
            <th className="px-3 py-2 font-medium">Matched by</th>
            <th className="px-3 py-2 font-medium">ScreenConnect</th>
            <th className="px-3 py-2 font-medium">Flags</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-divider">
          {shown.length === 0 && <tr><td colSpan={8} className="px-3 py-10 text-center text-muted-foreground">Nothing matches.</td></tr>}
          {shown.map((d) => (
            // Several Intune records can match one RMM device: the server then emits one row per record under the same key.
            <tr key={`${d.key}|${d.intune?.id ?? ''}`} className="align-top hover:bg-muted/30">
              <td className="overflow-hidden px-3 py-2">
                <Link to={insightsDevicePath(d.key)} className="block truncate font-medium text-foreground hover:underline" title={`${d.hostname}: Device Orbit`}>{d.hostname}</Link>
                <div className="truncate text-[11px] text-muted-foreground" title={d.os ?? ''}>{d.os ?? '—'}{d.serial && ` · ${d.serial}`}</div>
              </td>
              <td className="overflow-hidden px-3 py-2 text-xs">
                {d.companyId != null ? <Link to={companyPath(d.companyId)} className="block truncate text-foreground hover:underline" title={d.companyName ?? ''}>{d.companyName}</Link>
                  : <span className="italic text-muted-foreground">not mapped</span>}
              </td>
              <td className="px-3 py-2">
                <span className={cn('whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold', STATE_CHIP[d.state].cls)}>
                  {STATE_CHIP[d.state].label}{d.state === 'drift' && d.driftHours != null && ` ${fmtHours(d.driftHours)}`}
                </span>
              </td>
              <td className="px-3 py-2 text-xs">
                {d.rmm ? <span className={d.rmm.online ? 'text-success' : 'text-muted-foreground'}>{d.rmm.online ? 'Online' : `Seen ${fmtAge(d.rmm.lastSeen)} ago`}</span> : <span className="text-muted-foreground">—</span>}
              </td>
              <td className="overflow-hidden px-3 py-2 text-xs">
                {d.intune ? (
                  <>
                    <span className="whitespace-nowrap text-foreground">Synced {fmtAge(d.intune.lastSync)} ago</span>
                    {d.intune.compliance && d.intune.compliance !== 'compliant' && <span className="ml-1 whitespace-nowrap text-warning">· {d.intune.compliance}</span>}
                    {d.intune.user && <div className="truncate text-[11px] text-muted-foreground" title={d.intune.user}>{d.intune.user}</div>}
                  </>
                ) : <span className="text-muted-foreground">—</span>}
              </td>
              <td className="px-3 py-2 text-xs">
                {d.match ? (
                  <span title={d.match.confidence === 'high' ? 'High confidence' : 'Medium confidence — hostnames repeat and get reused'}
                    className={cn('whitespace-nowrap', d.match.confidence === 'high' ? 'text-foreground' : 'text-warning')}>
                    {d.match.by === 'mac' ? 'MAC' : d.match.by === 'serial' ? 'Serial' : 'Hostname'} · {d.match.confidence}
                  </span>
                ) : <span className="text-muted-foreground">—</span>}
              </td>
              <td className="px-3 py-2 text-xs"><RemoteCell r={d.remote} /></td>
              <td className="px-3 py-2 text-xs">
                {d.flags.map((f) => <div key={f} className="flex items-start gap-1 text-warning"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{f}</div>)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > shown.length && <div className="border-t border-divider px-3 py-2 text-xs text-muted-foreground">Showing 500 of {rows.length} — narrow with the search or a filter.</div>}
    </div>
  );
}

/** ScreenConnect for the device's Datto agent. */
function RemoteCell({ r }: { r: CoverageDevice['remote'] }) {
  if (!r || r.state === 'n/a') return <span className="text-muted-foreground">—</span>;
  if (r.state === 'missing') return <span className="whitespace-nowrap font-medium text-warning" title="In Datto RMM, but no ScreenConnect session matches it (serial, MAC or hostname)">No session</span>;
  if (r.state === 'unknown') return <span className="whitespace-nowrap text-muted-foreground" title="ScreenConnect machine details are still loading">checking…</span>;
  return <span className={cn('whitespace-nowrap', r.online ? 'text-success' : 'text-muted-foreground')}>{r.online ? '● Online' : '○ Offline'}</span>;
}

function CompanyTable({ data }: { data: NonNullable<ReturnType<typeof useDeviceCoverage>['data']> }) {
  return (
    <div className="mt-3 overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-3 py-2 font-medium">Client</th>
            <th className="px-3 py-2 font-medium">In both</th>
            <th className="px-3 py-2 text-right font-medium">Not in RMM</th>
            <th className="px-3 py-2 text-right font-medium">Not in Intune</th>
            <th className="px-3 py-2 text-right font-medium">Drift</th>
            <th className="px-3 py-2 text-right font-medium">Devices</th>
            <th className="px-3 py-2 font-medium">Uses</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-divider">
          {data.companies.map((c) => (
            <tr key={c.companyId} className="hover:bg-muted/30">
              <td className="px-3 py-2"><Link to={companyPath(c.companyId)} className="font-medium text-foreground hover:underline">{c.companyName}</Link></td>
              <td className="px-3 py-2">
                {c.pct == null ? <span className="text-xs text-muted-foreground">n/a — not using both</span> : (
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-32 overflow-hidden rounded-full bg-muted">
                      <div className={cn('h-full rounded-full', c.pct >= 90 ? 'bg-success' : c.pct >= 70 ? 'bg-warning' : 'bg-destructive')} style={{ width: `${Math.max(c.pct, 2)}%` }} />
                    </div>
                    <span className="text-xs tabular-nums">{c.pct}%</span>
                  </div>
                )}
              </td>
              <Num n={c.intuneOnly} bad />
              <Num n={c.rmmOnly} />
              <Num n={c.drift} />
              <td className="px-3 py-2 text-right tabular-nums">{c.devices}</td>
              <td className="px-3 py-2 text-xs text-muted-foreground">{[c.usesRmm && 'RMM', c.usesIntune && 'Intune'].filter(Boolean).join(' + ') || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Num({ n, bad }: { n: number; bad?: boolean }) {
  return <td className={cn('px-3 py-2 text-right tabular-nums', n ? (bad ? 'font-medium text-destructive' : 'font-medium text-warning') : 'text-muted-foreground')}>{n || '—'}</td>;
}

function fmtHours(h: number) {
  return h >= 48 ? `${Math.round(h / 24)}d` : `${h}h`;
}
